import { useEffect, useId, useRef, useState } from 'react'
import type { KataId } from '../../data/catalog/types'
import { useI18n } from '../../i18n'
import { PersonalDataPanel } from './PersonalDataPanel'
import styles from './NotesOverlay.module.css'

type Props = {
  kataId: KataId
  techniqueId: string
  techniqueName: string
  onFavouriteChange?: (techniqueId: string, favourite: boolean) => void
  /** Hide the FAB when no technique is selected. */
  disabled?: boolean
}

function PencilIcon() {
  return (
    <svg
      className={styles.fabIcon}
      viewBox="0 0 24 24"
      width="22"
      height="22"
      aria-hidden="true"
      focusable="false"
    >
      <path
        fill="currentColor"
        d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zm17.71-10.04a1.003 1.003 0 0 0 0-1.42l-2.5-2.5a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.99-1.66z"
      />
    </svg>
  )
}

/**
 * Bottom-right notes FAB that opens a fullscreen editor dialog.
 */
export function NotesOverlay({
  kataId,
  techniqueId,
  techniqueName,
  onFavouriteChange,
  disabled = false,
}: Props) {
  const { t } = useI18n()
  const [open, setOpen] = useState(false)
  const dialogRef = useRef<HTMLDialogElement>(null)
  const titleId = useId()
  const dialogId = useId()
  const tooltipId = useId()
  const fabRef = useRef<HTMLButtonElement>(null)
  const wasOpen = useRef(false)

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return

    if (open) {
      if (!dialog.open) dialog.showModal()
      wasOpen.current = true
    } else if (dialog.open) {
      dialog.close()
    }
  }, [open])

  useEffect(() => {
    if (open || !wasOpen.current) return
    wasOpen.current = false
    fabRef.current?.focus()
  }, [open])

  if (disabled) return null

  const label = t('personal.fullScreenNote')

  return (
    <>
      {!open && (
        <div className={styles.fabWrap}>
          <span id={tooltipId} className={styles.tooltip} role="tooltip">
            {label}
          </span>
          <button
            ref={fabRef}
            type="button"
            className={styles.fab}
            aria-label={label}
            aria-describedby={tooltipId}
            aria-haspopup="dialog"
            aria-controls={dialogId}
            onClick={() => setOpen(true)}
          >
            <PencilIcon />
          </button>
        </div>
      )}

      <dialog
        id={dialogId}
        ref={dialogRef}
        className={styles.dialog}
        aria-labelledby={titleId}
        onClose={() => setOpen(false)}
        onCancel={(event) => {
          event.preventDefault()
          setOpen(false)
        }}
      >
        <div className={styles.sheet}>
          <div className={styles.atmosphere} aria-hidden="true">
            <span className={styles.washA} />
            <span className={styles.washB} />
            <span className={styles.washC} />
          </div>

          <header className={styles.header}>
            <div className={styles.heading}>
              <p className={styles.eyebrow}>{t('personal.showNotes')}</p>
              <h2 id={titleId} className={styles.title}>
                {t('personal.notesPanel')}
              </h2>
              <p className={styles.subtitle}>{techniqueName}</p>
            </div>
            <button
              type="button"
              className={`btn btn-secondary ${styles.closeBtn}`}
              onClick={() => setOpen(false)}
            >
              {t('personal.closeNotes')}
            </button>
          </header>

          <div className={styles.body}>
            <PersonalDataPanel
              kataId={kataId}
              techniqueId={techniqueId}
              techniqueName={techniqueName}
              key={techniqueId}
              variant="fullscreen"
              onFavouriteChange={onFavouriteChange}
            />
          </div>
        </div>
      </dialog>
    </>
  )
}
